import { useState } from 'react'
import { FileText, Download, BarChart3, Users, Activity, ShieldCheck, CreditCard, GitBranch } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import autoTable from 'jspdf-autotable'
import { createThaiPDF, thaiTableStyles, thaiHeadStyles } from '@/src/lib/pdf/thai-font'
import {
  generateMonthlyRevenueReport,
  generateCustomerGrowthReport,
  generateSystemPerformanceReport,
  generateVerificationSummaryReport,
  generateCreditBillingReport,
  generateBankUsageCreditsReport,
  generateKycPipelineReport,
} from '@/src/lib/api/admin-reports'
import type { ReportDateRange } from '@/src/types/admin/reports'

interface ReportMeta {
  id: string
  title: string
  description: string
  audience: string
  icon: React.ElementType
  fn: (range: ReportDateRange) => Promise<unknown>
}

const REPORTS: ReportMeta[] = [
  {
    id: 'monthly_revenue',
    title: 'รายได้รายเดือน',
    description: 'MRR, breakdown ตามแพ็กเกจ และ growth rate',
    audience: 'ผู้บริหาร',
    icon: BarChart3,
    fn: generateMonthlyRevenueReport,
  },
  {
    id: 'customer_growth',
    title: 'การเติบโตของลูกค้า',
    description: 'ลูกค้าใหม่, churned, KYC funnel, สัดส่วนแพ็กเกจ',
    audience: 'ผู้บริหาร / การตลาด',
    icon: Users,
    fn: generateCustomerGrowthReport,
  },
  {
    id: 'system_performance',
    title: 'ประสิทธิภาพระบบ',
    description: 'Bank API health, SLA, cache stats',
    audience: 'เทคนิค',
    icon: Activity,
    fn: generateSystemPerformanceReport,
  },
  {
    id: 'verification_summary',
    title: 'สรุปการยืนยันสลิป',
    description: 'Success/fail ตามธนาคาร, จำนวนเงิน, เวลาประมวลผล',
    audience: 'ผู้บริหาร / เทคนิค',
    icon: ShieldCheck,
    fn: generateVerificationSummaryReport,
  },
  {
    id: 'credit_billing',
    title: 'Credit & Billing',
    description: 'เครดิตที่ใช้, ซื้อ, หมดอายุ รายเดือน',
    audience: 'ผู้บริหาร',
    icon: CreditCard,
    fn: generateCreditBillingReport,
  },
  {
    id: 'bank_usage_credits',
    title: 'เครดิตที่ใช้รายธนาคาร',
    description: 'สรุปการใช้เครดิตแยกตามธนาคารในช่วงวันที่เลือก',
    audience: 'ผู้บริหาร / เทคนิค',
    icon: Activity,
    fn: generateBankUsageCreditsReport,
  },
  {
    id: 'kyc_pipeline',
    title: 'KYC Pipeline',
    description: 'สถานะ KYC ตามประเภทและเดือน, เวลาเฉลี่ย',
    audience: 'Operations',
    icon: GitBranch,
    fn: generateKycPipelineReport,
  },
]

function toLocalISODate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function defaultRange(): ReportDateRange {
  const now = new Date()
  // First day of the current month
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  // Last day of the current month (day 0 of next month)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    date_from: toLocalISODate(from),
    date_to: toLocalISODate(to),
  }
}

function flattenRows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[]
  if (data && typeof data === 'object') {
    // Try to find first array property
    const d = data as Record<string, unknown>
    for (const key of Object.keys(d)) {
      if (Array.isArray(d[key])) return d[key] as Record<string, unknown>[]
    }
    return [d]
  }
  return []
}

/**
 * Build export rows for the Credit & Billing report.
 * The generic flattenRows only picks up monthly_breakdown and drops the
 * summary blocks, so we assemble the period totals explicitly here so the
 * exported file reflects the sum for the selected time range.
 */
function creditBillingRows(data: unknown): Record<string, unknown>[] {
  if (!data || typeof data !== 'object') return []
  const d = data as {
    monthly_breakdown?: Array<Record<string, unknown>>
    period_summary?: {
      by_type?: Array<{ transaction_type: string; count: number; total_amount: number }>
      total_count?: number
      total_amount?: number
    }
    current_balance_summary?: { total_balance: number; customer_count: number; avg_balance: number }
  }

  const rows: Record<string, unknown>[] = []

  // Monthly breakdown rows
  for (const r of d.monthly_breakdown ?? []) {
    rows.push({ section: 'รายเดือน', ...r })
  }

  // Period totals by transaction type (the summary block for the selected range)
  for (const t of d.period_summary?.by_type ?? []) {
    rows.push({
      section: 'สรุปช่วงเวลา (ตามประเภท)',
      transaction_type: t.transaction_type,
      count: t.count,
      total_amount: t.total_amount,
    })
  }

  // Overall period total
  if (d.period_summary) {
    rows.push({
      section: 'สรุปช่วงเวลา (รวมทั้งหมด)',
      transaction_type: 'ALL',
      count: d.period_summary.total_count ?? 0,
      total_amount: d.period_summary.total_amount ?? 0,
    })
  }

  // Current live balance snapshot
  if (d.current_balance_summary) {
    rows.push({
      section: 'ยอดคงเหลือปัจจุบัน (ณ ปัจจุบัน)',
      total_balance: d.current_balance_summary.total_balance,
      customer_count: d.current_balance_summary.customer_count,
      avg_balance: d.current_balance_summary.avg_balance,
    })
  }

  return rows
}

function exportExcel(rows: Record<string, unknown>[], filename: string) {
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Report')
  XLSX.writeFile(wb, `${filename}.xlsx`)
}

async function exportPdf(rows: Record<string, unknown>[], title: string, filename: string) {
  const doc = await createThaiPDF()
  doc.setFontSize(14)
  doc.text(title, 14, 16)
  if (rows.length > 0) {
    const columns = Object.keys(rows[0]).map((k) => ({ header: k, dataKey: k }))
    autoTable(doc, {
      startY: 24,
      columns,
      body: rows as import('jspdf-autotable').RowInput[],
      styles: { ...thaiTableStyles },
      headStyles: { ...thaiHeadStyles, fillColor: [99, 102, 241] },
    })
  }
  doc.save(`${filename}.pdf`)
}

function ReportCard({ report }: { report: ReportMeta }) {
  const [range, setRange] = useState<ReportDateRange>(defaultRange)
  const [loading, setLoading] = useState(false)
  const Icon = report.icon

  const generate = async (format: 'excel' | 'pdf') => {
    setLoading(true)
    try {
      const result = await report.fn(range)
      const reportResult = result as { data: unknown }
      const rows = report.id === 'credit_billing'
        ? creditBillingRows(reportResult.data)
        : flattenRows(reportResult.data)
      if (rows.length === 0) {
        toast.info('ไม่มีข้อมูลในช่วงวันที่ที่เลือก')
        return
      }
      const filename = `${report.id}_${range.date_from}_${range.date_to}`
      if (format === 'excel') {
        exportExcel(rows, filename)
      } else {
        exportPdf(rows, report.title, filename)
      }
      toast.success(`ดาวน์โหลด ${format.toUpperCase()} สำเร็จ`)
    } catch {
      toast.error('ไม่สามารถสร้างรายงานได้')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
            <CardTitle className="text-base leading-tight">{report.title}</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs shrink-0">{report.audience}</Badge>
        </div>
        <CardDescription>{report.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">วันที่เริ่ม</Label>
            <Input
              type="date"
              value={range.date_from}
              onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">วันที่สิ้นสุด</Label>
            <Input
              type="date"
              value={range.date_to}
              onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))}
              className="h-8 text-sm"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => generate('excel')}
            disabled={loading}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => generate('pdf')}
            disabled={loading}
          >
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default function AdminReports() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports Center</h1>
        <p className="text-muted-foreground">
          สร้างและดาวน์โหลดรายงาน Excel / PDF สำหรับผู้บริหาร Operations และเทคนิค
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map((r) => (
          <ReportCard key={r.id} report={r} />
        ))}
      </div>
    </div>
  )
}

